/**
 * 1.4.0 asset groups, stream G3's data hooks: the Grouped view, one level at a time (GET /api/v1/assets/grouped with
 * the Library's filters, parent=root|group:<id>, keyset pages of GROUPED_PAGE_SIZE). Keys: queryKeys.grouped() (not
 * under assets(); api/events.ts invalidates queryKeys.groupedAll() when assets or groups change). G3's file.
 */
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { AssetSummary, GroupedPageDTO, GroupedParent } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { libraryParams, type LibraryFilters } from './library';

/** The query string of the Library's filters as the Grouped view sends them (also its levels' key). */
export const groupedParams = (filters: LibraryFilters) => libraryParams(filters).toString();

export function useGroupedLevel(filters: LibraryFilters, parent: GroupedParent, enabled = true) {
  const params = groupedParams(filters);
  return useInfiniteQuery({
    queryKey: queryKeys.grouped(params, parent),
    // The signal: a part still running when the view is left (no observer) or stopped (cancelQueries) is aborted, and
    // the server cancels its statement (RequestDb).
    queryFn: ({ pageParam, signal }) => api<GroupedPageDTO>(`/api/v1/assets/grouped?${params}&parent=${encodeURIComponent(parent)}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // The root keeps its rows while a new search loads (no flash of "Loading…"); an opened group shows its own.
    placeholderData: parent === 'root' ? keepPreviousData : undefined,
    enabled,
  });
}

/** GET …/grouped?parent=group:<id>&select=assets: a group's matching assets (its sub-groups' too), up to 1,000. */
/** incomplete: the server could not find them in time (a slow database); nothing is selected. */
export interface GroupedSelectDTO { items: AssetSummary[]; truncated: boolean; incomplete?: boolean }
export function fetchGroupSelection(filters: LibraryFilters, groupId: string): Promise<GroupedSelectDTO> {
  return api<GroupedSelectDTO>(`/api/v1/assets/grouped?${groupedParams(filters)}&parent=${encodeURIComponent(`group:${groupId}`)}&select=assets`);
}
